'use client';

import * as React from 'react';
import { cn } from './cn';
import { useControllableState, useElementDirection, useId } from './hooks';

export interface SliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  /** Fires on every change (drag frame + key press). */
  onValueChange?: (value: number) => void;
  /** Fires once when the interaction ends — use this to close an undo coalescing window. */
  onValueCommit?: (value: number) => void;
  disabled?: boolean;
  label?: React.ReactNode;
  /** Formats the readout. Default: the raw number. */
  formatValue?: (value: number) => string;
  /** Show the numeric readout beside the label. Default true when `label` is set. */
  showValue?: boolean;
  /**
   * Replace the read-only readout with a numeric field, so a precise value can be typed instead of
   * dragged (docs/10-ux-audit.md · UX-19). Opt-in: a plain slider is unchanged.
   */
  editable?: boolean;
  /** Unit shown after the numeric field — `pt`, `mm`, `%`. Announced as its description. */
  unit?: React.ReactNode;
  /** Accessible name for the numeric field when there is no `label`. */
  editLabel?: string;
  /** Show a bubble above the thumb while dragging. */
  bubble?: boolean;
  /**
   * Where the filled part starts. `min` (default) for 0→100 ranges like opacity,
   * `center` for bipolar adjustments like brightness (-100…100).
   */
  origin?: 'min' | 'center' | number;
  /** Tick marks drawn on the track. */
  marks?: number[];
  /** Double-click / double-tap resets to this value. */
  resetValue?: number;
  size?: 'sm' | 'md';
  name?: string;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function quantize(raw: number, min: number, max: number, step: number): number {
  const snapped = Math.round((raw - min) / step) * step + min;
  const decimals = (String(step).split('.')[1] ?? '').length;
  return Number(clamp(snapped, min, max).toFixed(decimals));
}

/** `12.50` → `12.5`, `12.00` → `12`. The field shows a number, not a formatted string. */
export function formatSliderNumber(value: number, step: number): string {
  const decimals = (String(step).split('.')[1] ?? '').length;
  return String(Number(value.toFixed(decimals)));
}

/**
 * Reads what the user typed into the numeric field.
 *
 * Returns `null` when the text is not a number at all — the caller then keeps the previous value
 * instead of collapsing to `min`, so a typo never destroys the setting. Anything readable is
 * clamped into range and snapped to `step`, which is why `999` in a 6–120 field becomes `120`.
 * A comma is accepted as a decimal separator: Hebrew keyboards sit next to European ones.
 */
export function parseSliderInput(raw: string, min: number, max: number, step: number): number | null {
  const cleaned = raw.replace(/[\u200e\u200f\s]/g, '').replace(',', '.');
  if (cleaned === '' || !/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return quantize(n, min, max, step);
}

/**
 * Single-value slider, hand-rolled for three reasons the editor needs:
 * pointer capture (drag can leave the track), RTL-correct maths, and a
 * 44 px touch strip around a 6 px visual track.
 *
 * ```tsx
 * <Slider label="שקיפות" min={0} max={100} value={opacity}
 *   onValueChange={setOpacity} onValueCommit={commit} formatValue={(v) => `${v}%`} />
 * ```
 *
 * `editable` swaps the readout for a typed value with its unit, for the precision the thumb cannot
 * reach on a phone. It is a real `<input role="spinbutton">`, so the editor's global shortcuts leave
 * its arrows and Delete alone.
 */
export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(function Slider(
  {
    value,
    defaultValue = 0,
    min = 0,
    max = 100,
    step = 1,
    onValueChange,
    onValueCommit,
    disabled,
    label,
    formatValue,
    showValue,
    editable,
    unit,
    editLabel,
    bubble = true,
    origin = 'min',
    marks,
    resetValue,
    size = 'md',
    name,
    className,
    'aria-label': ariaLabel,
    ...props
  },
  ref,
) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const dir = useElementDirection(wrapRef);
  const id = useId('slider');
  const [current, setCurrent] = useControllableState(value, defaultValue, onValueChange);
  const [dragging, setDragging] = React.useState(false);
  // While the field is being typed in it holds the raw text; `null` means "show the real value".
  const [draft, setDraft] = React.useState<string | null>(null);

  const format = formatValue ?? ((v: number) => String(v));
  const pct = ((current - min) / (max - min)) * 100;
  const originValue = origin === 'min' ? min : origin === 'center' ? (min + max) / 2 : origin;
  const originPct = ((originValue - min) / (max - min)) * 100;
  const fillStart = Math.min(pct, originPct);
  const fillSize = Math.abs(pct - originPct);

  const valueFromPointer = React.useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return current;
      let f = (clientX - rect.left) / rect.width;
      if (dir === 'rtl') f = 1 - f;
      return quantize(min + clamp(f, 0, 1) * (max - min), min, max, step);
    },
    [current, dir, max, min, step],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setCurrent(valueFromPointer(e.clientX));
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || disabled) return;
    e.preventDefault();
    setCurrent(valueFromPointer(e.clientX));
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    onValueCommit?.(valueFromPointer(e.clientX));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const big = Math.max(step, (max - min) / 10);
    const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    let next: number | null = null;
    if (e.key === forward || e.key === 'ArrowUp') next = current + step;
    else if (e.key === back || e.key === 'ArrowDown') next = current - step;
    else if (e.key === 'PageUp') next = current + big;
    else if (e.key === 'PageDown') next = current - big;
    else if (e.key === 'Home') next = min;
    else if (e.key === 'End') next = max;
    if (next === null) return;
    e.preventDefault();
    const v = quantize(next, min, max, step);
    setCurrent(v);
    onValueCommit?.(v);
  };

  const reset = () => {
    if (disabled || resetValue === undefined) return;
    setCurrent(resetValue);
    onValueCommit?.(resetValue);
  };

  // ── numeric field ───────────────────────────────────────────────────────────
  // One committed change per entry: `onValueChange` then `onValueCommit`, exactly like releasing
  // the thumb, so the editor records a single undoable step (docs/10-ux-audit.md · UX-19).
  const commitDraft = (raw: string) => {
    setDraft(null);
    const next = parseSliderInput(raw, min, max, step);
    if (next === null || next === current) return;
    setCurrent(next);
    onValueCommit?.(next);
  };

  const stepBy = (delta: number) => {
    const base = draft === null ? current : (parseSliderInput(draft, min, max, step) ?? current);
    const next = quantize(base + delta, min, max, step);
    setDraft(null);
    if (next === current) return;
    setCurrent(next);
    onValueCommit?.(next);
  };

  const onFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft(e.currentTarget.value);
    } else if (e.key === 'Escape') {
      // Abandon the typed text and go back to the value that is actually set, and let nothing else
      // in the tree treat the same key as its own cancel.
      e.preventDefault();
      e.stopPropagation();
      setDraft(null);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      stepBy(e.key === 'ArrowUp' ? step : -step);
    }
  };

  const field = (
    <span className="flex items-center gap-1">
      <input
        type="text"
        role="spinbutton"
        inputMode="decimal"
        dir="ltr"
        enterKeyHint="done"
        disabled={disabled}
        value={draft ?? formatSliderNumber(current, step)}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => commitDraft(e.currentTarget.value)}
        onKeyDown={onFieldKeyDown}
        onPointerDown={(e) => e.stopPropagation()}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={current}
        aria-valuetext={format(current)}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-label={label ? undefined : (editLabel ?? ariaLabel)}
        aria-describedby={unit ? `${id}-unit` : undefined}
        className={cn(
          'h-8 w-14 rounded-md border border-line bg-surface px-1 text-center text-sm tabular-nums text-ink',
          'transition-colors duration-(--duration-fast) hover:border-line-strong',
          'focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
          disabled && 'pointer-events-none opacity-45',
        )}
      />
      {unit ? (
        <span id={`${id}-unit`} className="text-xs text-ink-2">
          {unit}
        </span>
      ) : null}
    </span>
  );

  const trackH = size === 'sm' ? 'h-1' : 'h-1.5';
  const thumbPx = size === 'sm' ? 16 : 20;

  return (
    <div ref={wrapRef} className={cn('flex w-full flex-col gap-1', className)}>
      {(label || showValue || editable) && (
        <div className={cn('flex justify-between gap-3', editable ? 'items-center' : 'items-baseline')}>
          {label ? (
            <span id={`${id}-label`} className="text-sm font-medium text-ink">
              {label}
            </span>
          ) : (
            <span />
          )}
          {editable ? (
            field
          ) : (
            (showValue ?? Boolean(label)) && (
              <span className="text-xs tabular-nums text-ink-2" dir="ltr">
                {format(current)}
              </span>
            )
          )}
        </div>
      )}

      <div
        ref={ref}
        className={cn(
          'relative flex min-h-touch w-full cursor-pointer touch-none items-center py-4',
          disabled && 'pointer-events-none opacity-45',
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={reset}
        {...props}
      >
        {/* track */}
        <div ref={trackRef} className={cn('relative w-full rounded-full bg-paper-3', trackH)}>
          {marks?.map((m) => {
            const p = ((m - min) / (max - min)) * 100;
            return (
              <span
                key={m}
                aria-hidden
                className="absolute top-1/2 size-1 -translate-y-1/2 rounded-full bg-line-strong"
                style={{ insetInlineStart: `${p}%`, marginInlineStart: '-2px' }}
              />
            );
          })}
          {/* fill */}
          <div
            className={cn('absolute top-0 rounded-full bg-accent', trackH)}
            style={{ insetInlineStart: `${fillStart}%`, width: `${fillSize}%` }}
            aria-hidden
          />
          {/* thumb */}
          <div
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={current}
            aria-valuetext={format(current)}
            aria-orientation="horizontal"
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-label={label ? undefined : ariaLabel}
            aria-disabled={disabled || undefined}
            onKeyDown={onKeyDown}
            className={cn(
              'absolute top-1/2 rounded-full border-2 border-accent bg-surface-2 shadow-sm',
              'transition-[box-shadow,transform] duration-(--duration-fast) ease-out-soft',
              'hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              dragging && 'scale-110 shadow-md',
            )}
            style={{
              width: thumbPx,
              height: thumbPx,
              insetInlineStart: `${pct}%`,
              marginInlineStart: `-${thumbPx / 2}px`,
              marginTop: `-${thumbPx / 2}px`,
            }}
          >
            {bubble && dragging ? (
              <span
                dir="ltr"
                className={cn(
                  'text-2xs pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2',
                  'rounded-sm bg-ink px-1.5 py-0.5 font-medium tabular-nums text-ink-inverse shadow-md',
                )}
              >
                {format(current)}
              </span>
            ) : null}
          </div>
        </div>
        {name ? <input type="hidden" name={name} value={current} /> : null}
      </div>
    </div>
  );
});
